import { Button, List, LoadIndicator, RadioGroup, ScrollView, TagBox, TextBox } from "devextreme-react";
import { SetAllColumnsViewerByRole, fetcher, updateFeatureColumnsByRole } from "../../../../actions";
import useSWR from "swr";
import { useState, useCallback, useMemo } from "react";
import './ColumnsByRole.css';

const _ = require("lodash");

export const ColumnsByRole = (props) => {
    const { roleId } = props;
    const { isloading, data, mutate } = useSWR(`/api/RoleColumns/reportColumnPermissions?idRole=${roleId}`, fetcher);
    const [searchTerm, setSearchTerm] = useState("");

    const callUpdateFeatureColumnsByRole = useCallback(async (roleId, idColumn, e) => {
        await updateFeatureColumnsByRole(roleId, idColumn, e);
    }, []);

    const callSetColumnsViewerAllByRole = useCallback(async (roleId) => {
        await SetAllColumnsViewerByRole(roleId);
    }, []);

    const setPermissionValue = useCallback(async (e, item) => {
        // Optimistic update - only update the specific item
        const newdata = data.map((x) => {
            if (item.IdColumn === x.IdColumn) {
                return { ...x, Permission: e };
            }
            return x;
        });
        // Use optimistic update without revalidation
        mutate(newdata, false);
        // Update database in background
        await callUpdateFeatureColumnsByRole(roleId, item.IdColumn, e);
    }, [data, mutate, roleId, callUpdateFeatureColumnsByRole]);

    const setViewerAll = useCallback((roleId) => {
        const newdata = data.map((x) => {
            return { ...x, Permission: 1 };
        });
        mutate(newdata, false);
        callSetColumnsViewerAllByRole(roleId);
    }, [data, mutate, callSetColumnsViewerAllByRole]);
    
    const getPermissionColor = useCallback((permission) => {
        if (permission === 0) return 'default';
        if (permission === 1) return 'info';
        return 'success';
    }, []);
    
    const getPermissionIcon = useCallback((permission) => {
        if (permission === 0) return 'dx-icon-close';
        if (permission === 1) return 'dx-icon-eyeopen';
        return 'dx-icon-edit';
    }, []);

    const renderItem = useCallback((item) => {
        const writePermissions = [
            { id: 0, text: 'None' },
            { id: 1, text: 'Viewer' },
            { id: 2, text: 'Editor' }
        ];
        const readPermissions = [
            { id: 0, text: 'None' },
            { id: 1, text: 'Viewer' }
        ];
        let permissions = writePermissions;
        if (item.Editable === 0) {
            permissions = readPermissions;
        }
        
        const permissionClass = item.Permission === 0 ? 'badge-default' : 
                                item.Permission === 1 ? 'badge-info' : 'badge-success';
        const permissionText = item.Permission === 0 ? 'No Access' : 
                               item.Permission === 1 ? 'Viewer' : 'Editor';
        
        return (
            <div className="column-item">
                <div className="column-item-row">
                    <div className="column-item-left">
                        <div className="column-info">
                            <i className="dx-icon-columnfield" style={{ fontSize: 16, marginRight: 8 }}></i>
                            <span className="column-name">{item.NameColumn}</span>
                            <span className={`permission-badge ${permissionClass}`}>
                                <i className={getPermissionIcon(item.Permission)} style={{ fontSize: 12, marginRight: 4 }}></i>
                                {permissionText}
                            </span>
                            {item.Editable === 0 && (
                                <span className="badge-readonly">Read Only</span>
                            )}
                        </div>
                    </div>
                    <div className="column-item-right">
                        <RadioGroup 
                            id="radio-group-with-selection" 
                            items={permissions} 
                            value={item.Permission}
                            valueExpr="id" 
                            displayExpr="text" 
                            onValueChange={async (e) => await setPermissionValue(e, item)}
                            layout="horizontal"
                        />
                    </div>
                </div>
            </div>
        );
    }, [getPermissionIcon, setPermissionValue]);

    // Filter data based on search term - memoized to prevent recalculation
    const filteredData = useMemo(() => {
        if (!data) return [];
        if (!searchTerm) return data;
        return data.filter(item => 
            item.NameColumn.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [data, searchTerm]);
    
    // Calculate statistics - memoized
    const statistics = useMemo(() => ({
        totalColumns: data?.length || 0,
        editorCount: data?.filter(i => i.Permission === 2).length || 0,
        viewerCount: data?.filter(i => i.Permission === 1).length || 0,
        noAccessCount: data?.filter(i => i.Permission === 0).length || 0
    }), [data]);

    if (isloading) {
        return (
            <div className="loading-container">
                <LoadIndicator visible={true} />
                <div className="loading-text">Loading columns...</div>
            </div>
        );
    }

    return (
        <div id="ColumnsByRoleContainer" className="columns-container">
            {/* Header with stats */}
            <div className="stats-row">
                <span className="stat-badge badge-primary">
                    <i className="dx-icon-columnfield" />
                    {statistics.totalColumns} Columns
                </span>
                <span className="stat-badge badge-success">
                    <i className="dx-icon-edit" />
                    {statistics.editorCount} Editor
                </span>
                <span className="stat-badge badge-info">
                    <i className="dx-icon-eyeopen" />
                    {statistics.viewerCount} Viewer
                </span>
                <span className="stat-badge badge-default">
                    <i className="dx-icon-close" />
                    {statistics.noAccessCount} No Access
                </span>
            </div>

            {/* Description */}
            <div className="info-alert">
                <div className="info-alert-content">
                    <strong>Configure column visibility and edit permissions:</strong>
                    <div className="info-list">
                        • <strong>None:</strong> Column is hidden<br/>
                        • <strong>Viewer:</strong> Can view but not edit<br/>
                        • <strong>Editor:</strong> Can view and edit
                    </div>
                </div>
            </div>

            {/* Search and bulk actions */}
            <div className="actions-row">
                <div className="search-box">
                    <TextBox
                        placeholder="Search columns..."
                        value={searchTerm}
                        onValueChanged={(e) => setSearchTerm(e.value)}
                        mode="search"
                        width="100%"
                    />
                </div>
                <Button 
                    text="Set All to Viewer"
                    icon="eyeopen"
                    type="default"
                    onClick={() => setViewerAll(roleId)}
                />
            </div>

            {/* Columns list */}
            <div className="columns-list-container">
                <ScrollView width='100%' height='100%'>
                    <div className="list-container">
                        <List
                            dataSource={filteredData}
                            height="100%"
                            itemRender={renderItem}
                            keyExpr="IdColumn"
                            repaintChangesOnly={true}
                            pageLoadMode="scrollBottom"
                        />
                    </div>
                </ScrollView>
            </div>
        </div>
    );
}


export default ColumnsByRole; 